import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Shield } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyErotikaLogo } from '../components/MyErotikaLogo';

export default function PrivacyPolicyPage() {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "Politique de Confidentialité",
      lastUpdated: "Dernière mise à jour : Février 2025",
      intro: "Bienvenue sur MyErotika : Dialogues Intimes. Nous accordons une grande importance à la protection de vos données personnelles. Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations.",
      sections: [
        {
          title: "1. Informations collectées",
          content: `Nous collectons les informations suivantes :
• Informations de compte : adresse e-mail, nom d'utilisateur et mot de passe (crypté)
• Données d'utilisation : historique des conversations, préférences de personnages, niveaux débloqués
• Informations de paiement : traitées de manière sécurisée par Stripe (nous ne stockons pas vos données bancaires)`
        },
        {
          title: "2. Utilisation des données",
          content: `Vos données sont utilisées pour :
• Fournir et améliorer nos services
• Personnaliser votre expérience
• Traiter vos paiements
• Vous envoyer des communications importantes concernant votre compte`
        },
        {
          title: "3. Protection des données",
          content: `Nous mettons en œuvre des mesures de sécurité appropriées :
• Chiffrement des mots de passe
• Connexions sécurisées (HTTPS)
• Accès restreint aux données personnelles
• Stockage sécurisé des données`
        },
        {
          title: "4. Partage des données",
          content: `Nous ne vendons jamais vos données personnelles. Nous pouvons partager des informations avec :
• Stripe pour le traitement des paiements
• OpenAI pour générer les réponses de l'IA (conversations anonymisées)
• Les autorités si requis par la loi`
        },
        {
          title: "5. Vos droits",
          content: `Vous avez le droit de :
• Accéder à vos données personnelles
• Rectifier vos informations
• Supprimer votre compte et vos données
• Exporter vos données
• Retirer votre consentement à tout moment`
        },
        {
          title: "6. Conservation des données",
          content: "Nous conservons vos données aussi longtemps que votre compte est actif. Vous pouvez demander la suppression de vos données à tout moment en nous contactant."
        },
        {
          title: "7. Cookies",
          content: "Nous utilisons des cookies essentiels pour le fonctionnement de l'application (authentification, préférences de langue). Aucun cookie de tracking publicitaire n'est utilisé."
        },
        {
          title: "8. Contenu pour adultes",
          content: "MyErotika est une application destinée aux adultes (18+). En utilisant cette application, vous confirmez avoir l'âge légal requis dans votre pays de résidence."
        },
        {
          title: "9. Contact",
          content: "Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, contactez-nous à : privacy@erotika-app.com"
        },
        {
          title: "10. Modifications",
          content: "Nous pouvons mettre à jour cette politique de confidentialité. Les modifications seront publiées sur cette page avec une date de mise à jour."
        }
      ]
    },
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: February 2025",
      intro: "Welcome to MyErotika: Intimate Dialogues. We place great importance on protecting your personal data. This privacy policy explains how we collect, use, and protect your information.",
      sections: [
        {
          title: "1. Information Collected",
          content: `We collect the following information:
• Account information: email address, username, and password (encrypted)
• Usage data: conversation history, character preferences, unlocked levels
• Payment information: securely processed by Stripe (we do not store your banking data)`
        },
        {
          title: "2. Use of Data",
          content: `Your data is used to:
• Provide and improve our services
• Personalize your experience
• Process your payments
• Send you important communications about your account`
        },
        {
          title: "3. Data Protection",
          content: `We implement appropriate security measures:
• Password encryption
• Secure connections (HTTPS)
• Restricted access to personal data
• Secure data storage`
        },
        {
          title: "4. Data Sharing",
          content: `We never sell your personal data. We may share information with:
• Stripe for payment processing
• OpenAI to generate AI responses (anonymized conversations)
• Authorities if required by law`
        },
        {
          title: "5. Your Rights",
          content: `You have the right to:
• Access your personal data
• Correct your information
• Delete your account and data
• Export your data
• Withdraw your consent at any time`
        },
        {
          title: "6. Data Retention",
          content: "We retain your data as long as your account is active. You can request deletion of your data at any time by contacting us."
        },
        {
          title: "7. Cookies",
          content: "We use essential cookies for the application to function (authentication, language preferences). No advertising tracking cookies are used."
        },
        {
          title: "8. Adult Content",
          content: "MyErotika is an application intended for adults (18+). By using this application, you confirm that you are of legal age in your country of residence."
        },
        {
          title: "9. Contact",
          content: "For any questions about this privacy policy or to exercise your rights, contact us at: privacy@erotika-app.com"
        },
        {
          title: "10. Changes",
          content: "We may update this privacy policy. Changes will be posted on this page with an updated date."
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
          >
            <ArrowLeft size={20} />
            <MyErotikaLogo size="sm" />
          </Link>
          <LanguageSelector />
        </div>
      </header>

      {/* Content */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-purple-main/20 flex items-center justify-center">
              <Shield className="text-purple-light" size={32} />
            </div>
            <h1 className="font-heading text-4xl gradient-text mb-4">{t.title}</h1>
            <p className="text-velvet_text-secondary text-sm">{t.lastUpdated}</p>
          </div>

          <div className="glass rounded-2xl p-8 mb-8">
            <p className="text-velvet_text-secondary leading-relaxed">{t.intro}</p>
          </div>

          <div className="space-y-6">
            {t.sections.map((section, index) => (
              <div key={index} className="glass rounded-xl p-6">
                <h2 className="font-heading text-xl text-velvet_text-primary mb-4">{section.title}</h2>
                <p className="text-velvet_text-secondary leading-relaxed whitespace-pre-line">{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-velvet_text-secondary text-sm">
            © 2025 MyErotika. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
